import { Badge, Icon, Tooltip, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import isEmpty from 'lodash/isEmpty';
import maxBy from 'lodash/maxBy';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

import { queryClient } from '@hero/clients';
import { AppLoader } from '@hero/components/atoms';
import { DuplicateModal } from '@hero/components/modals';
import {
  AUTOMATION_STATUSES,
  AVAILABLE_OCCASION_NAMES,
  AUTOMATION_LIST_TABLE_LIMIT,
  PAYMENT_METHOD_TYPES,
  AUTOMATION_CREATION_STEPS,
  NOTIFICATION_TYPE,
  ACTIVE_AUTOMATION_FILTERS_BY_STATUS,
  AUTOMATION_FILTER_STATUSES,
  CARDISLE_GREETING_CARD,
  CONTACT_TYPE
} from '@hero/constants';
import {
  useIsAdminOrganization,
  useAutomationTableFilterOptions,
  useDeleteModalTemplate
} from '@hero/hooks';
import { OptionsDropdown, OrderStatusBadge, Table, UserName } from '@hero/molecules';
import { TableProducts } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetTagList } from '@hero/services/contact-service/hooks';
import {
  automationServices,
  useDeleteAutomation,
  useDuplicateAutomation,
  useGetAutomations,
  useUpdateAutomation
} from '@hero/services/order-service';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useNewAutomationStore, useNotification } from '@hero/store/hooks';
import { formatCurrency, formatNumber, getAddonData } from '@hero/utils';
import { automationReactivateSchema } from '@hero/validations';

import { reactivateAutomationFormData } from '../constants';

import { EmptyAutomationTable } from './EmptyTable';
import { Filter } from './Filter';
import { SecondHead } from './SecondHead';
import * as S from './style';

function AutomationTable() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const location = useLocation();

  const { data: user } = useGetUser();

  const {
    updateName,
    updateType,
    updateSettings,
    updateExperienceSettings,
    updateMeta,
    updateContacts,
    updateProducts,
    updateCode,
    updateAddons
  } = useNewAutomationStore();
  const { mutateAsync: updateAutomation } = useUpdateAutomation();
  const { mutateAsync: duplicateAutomation } = useDuplicateAutomation();
  const automationFilterOptions = useAutomationTableFilterOptions();
  const isOrganizationAdmin = useIsAdminOrganization();
  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('automation').toLowerCase() }),
    message: t('deleteAutomationModalDesc'),
    confirmText: 'DELETE AUTOMATION',
    maxLength: 17
  });
  const { mutateAsync: deleteAutomation } = useDeleteAutomation();

  const [duplicateModalData, setDuplicateModalData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: AUTOMATION_LIST_TABLE_LIMIT
  });
  const [globalFilters, setGlobalFilters] = useState({
    searchTerm: '',
    filters: {
      status: [],
      type: [],
      createdBy: []
    }
  });

  const {
    data: automationList,
    isLoading: isAutomationListLoading,
    refetch
  } = useGetAutomations({
    filters: {
      searchTerm: globalFilters.searchTerm,
      status: Array.from(
        new Set(globalFilters.filters?.status?.map(f => AUTOMATION_FILTER_STATUSES[f.value]))
      ),
      type: Array.from(
        new Set(
          globalFilters.filters?.type?.map(f => Object.keys(AVAILABLE_OCCASION_NAMES)[f.value])
        )
      ),
      meta: {
        creationSteps: globalFilters?.filters?.status?.reduce(
          (r, f) => [...r, ...(ACTIVE_AUTOMATION_FILTERS_BY_STATUS[f.value] || [])],
          []
        )
      },
      ...(!!globalFilters?.filters?.createdBy &&
        !!isOrganizationAdmin && {
          createdBy: globalFilters?.filters?.createdBy?.map(f => f?.value)?.join(',')
        }),
      ...location?.state
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  const { data: tagListData } = useGetTagList(
    {
      options: {
        includeContacts: true
      }
    },
    {
      enabled: !!automationList?.docs
    }
  );

  const renderProducts = ({ row }) => {
    return <TableProducts products={row.original?.products} />;
  };

  const onDeactivateModal = row => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      title: t('areYouSure'),
      message:
        row.original?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard ? (
          <Trans i18nKey="expireAutomationPromptMessageCC" components={{ b: <b /> }} />
        ) : (
          <Trans i18nKey="expireAutomationPromptMessageHouseAccount" components={{ b: <b /> }} />
        ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('noKeepActive') },
      primaryButtonProps: { color: 'error', children: t('yesDeactivate') },
      onSubmit: async () => {
        await updateAutomation({
          params: { code: row.original.code },
          body: {
            status: AUTOMATION_STATUSES.deactivated
          }
        });
        refetch();
      }
    });
  };

  const onReactivateModal = async row => {
    setIsLoading(true);
    const spendingSummary = await queryClient.fetchQuery(
      ['automationSpendingSummary', row.original.code],
      () => automationServices.spendingSummary(row.original.code)
    );

    setIsLoading(false);
    showNotification({
      notification: {
        title: (
          <Typography as="div" variant="body2Bold" style={{ display: 'flex', gap: 4 }}>
            {t('totalEstimatedSpend')}
            <Typography variant="body2" color="fg" emphasis="weak">
              ({t('perRecipient').toLocaleLowerCase()})
            </Typography>
          </Typography>
        ),
        message: (
          <Typography variant="body2Bold">
            {formatCurrency(
              spendingSummary.estimatedCharges.total - spendingSummary.spendingSummary.total
            )}
          </Typography>
        ),
        status: 'info',
        isClosable: true
      },
      message: t('reactivateModalDescRedemption'),
      notificationImage: <Icon name="calendar" size="xxl" />,
      formData: reactivateAutomationFormData,
      formValidationSchema: automationReactivateSchema,
      formStyle: {
        background: 'var(--color-bg-default)',
        borderRadius: 'var(--radius-m)',
        border: '1px solid var(--color-accent-secondary-weaker)',
        padding: 16
      },
      primaryButtonProps: { children: t('reactivate') },
      secondaryButtonProps: { children: t('cancel') },
      onSubmit: async ({ formValues }) => {
        await updateAutomation({
          params: { code: row.original.code },
          body: {
            settings: {
              acceptanceDurationInHours: formValues?.redemptionWindow,
              occasionScheduleDuration: {
                unit: 'day',
                value: formValues?.occasionScheduleDuration
              }
            },
            status: AUTOMATION_STATUSES.active
          }
        });
        await refetch();
        showNotification({
          type: NOTIFICATION_TYPE.toast,
          message: <Trans i18nKey="automationReactivated" components={{ b: <b /> }} />
        });
      }
    });
  };

  const onNavigateToDetailPage = row => {
    if (row.original?.status === AUTOMATION_STATUSES.draft) {
      if (
        row?.original?.meta?.creationStep &&
        row?.original?.meta?.creationStep !== AUTOMATION_CREATION_STEPS.newAutomationReviewActive
      ) {
        updateContacts({
          ...row.original.contacts,
          maxCount: row.original?.contacts?.maxCount?.toString() || '0'
        });
        updateName(row.original.name);
        updateType(row.original.type);
        updateCode(row.original.code);
        updateExperienceSettings(row.original.experienceSettings || {});
        updateMeta(row.original.meta);
        updateSettings(row.original.settings);
        updateProducts(row.original?.products);
        updateAddons(row.original?.addons);

        navigate(ROUTE_PATHS[row.original?.meta?.creationStep]);
      } else {
        navigate(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE(row.original.code), {
          search: `status=${row.original.status}`
        });
      }
    } else {
      navigate({
        pathname: ROUTE_PATHS.MY_AUTOMATION_DETAIL(row.original.code),
        search: `?${createSearchParams({
          status: row.original?.status
        })}`
      });
    }
  };

  const renderTagContactCount = ({ row }) => {
    const tags = tagListData?.docs.filter(tag => row.original?.contacts?.tags?.includes(tag.id));
    const isTagSelected =
      !isEmpty(row.original?.contacts?.tags) && isEmpty(row.original?.contacts?.ids);

    let contactsCount = 0;

    if (isTagSelected) {
      tags?.forEach(tag => {
        contactsCount += tag.contactsCount;
      });
    } else {
      contactsCount = row.original.contacts?.ids?.length;
    }

    return (
      <Typography variant="body2" as="div" style={{ display: 'flex', alignItems: 'center' }}>
        {contactsCount}
        {row.original?.contacts?.tags?.length > 0 && (
          <Tooltip
            content={`${t('automationTableTagTooltipContent')}: ${tags?.map(
              tag => `"${tag.name}" `
            )}`}
            tooltipConfig={{ placement: 'top' }}
          >
            <Badge
              variant="text"
              status="active"
              size="sm"
              style={{ background: 'var(--color-bg-weaker)' }}
            >
              <Icon name="tag" color="var(--color-fg-weaker)" />
              <Typography variant="caption1Bold" emphasis="weaker">
                {t('amountOfTags', { amount: tags?.length })}
              </Typography>
            </Badge>
          </Tooltip>
        )}
      </Typography>
    );
  };

  const renderOccasionType = ({ row }) => {
    return t(AVAILABLE_OCCASION_NAMES[row.original?.type]);
  };

  const onDeleteModal = async row => {
    showDeleteModal({
      onSubmit: async () => {
        await deleteAutomation(row.original.code);
      }
    });
  };

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorKey: 'name',
        size: 230,
        cell: ({ getValue, row }) => (
          <Typography
            onClick={() => onNavigateToDetailPage(row)}
            variant="body2"
            as="p"
            lineClamp={1}
            underline
            style={{ cursor: 'pointer' }}
            title={row.original.name}
          >
            {getValue()}
          </Typography>
        )
      },
      ...(isOrganizationAdmin
        ? [
            {
              id: 'sender',
              header: t('createdBy'),
              cell: ({ cell: { row } }) => {
                return (
                  <UserName
                    firstName={row.original.createdBy.firstName}
                    lastName={row.original.createdBy.lastName}
                  />
                );
              }
            }
          ]
        : []),
      {
        header: t('gifts'),
        accessorKey: 'products',
        cell: renderProducts
      },
      {
        header: `# ${t('recipients')}`,
        accessorKey: 'numberOfContacts',
        cell: renderTagContactCount
      },
      {
        header: t('automationType'),
        accessorKey: 'type',
        cell: renderOccasionType
      },
      {
        header: t('redemption'),
        accessorFn: row =>
          ((row?.acceptanceRates?.accepted * 100) / row?.acceptanceRates?.total || 0).toFixed(),
        sortType: 'number',
        cell: ({ getValue }) => `${getValue()}%`
      },
      {
        header: t('status'),
        accessorKey: 'status',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isReadyToActivate =
            row.original.status === AUTOMATION_STATUSES.draft &&
            row.original?.meta?.creationStep ===
              AUTOMATION_CREATION_STEPS.newAutomationReviewActive;

          return (
            <OrderStatusBadge
              status={isReadyToActivate ? AUTOMATION_STATUSES.readyToActivate : value}
            />
          );
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => {
          const isDraft =
            row.original.status === AUTOMATION_STATUSES.draft &&
            row.original?.meta?.creationStep !==
              AUTOMATION_CREATION_STEPS.newAutomationReviewActive;
          const isReadyToActivate =
            row.original.status === AUTOMATION_STATUSES.draft &&
            row.original?.meta?.creationStep ===
              AUTOMATION_CREATION_STEPS.newAutomationReviewActive;
          const isActive = row.original.status === AUTOMATION_STATUSES.active;
          const isDeactivated = row.original.status === AUTOMATION_STATUSES.deactivated;

          const skus = row.original.products.reduce((result, product) => {
            return [...result, ...product.skus];
          }, []);

          const options = [
            {
              label: t(isDraft ? 'keepEditing' : 'seeDetails'),
              onClick: () => onNavigateToDetailPage(row)
            },
            {
              label: t('duplicate'),
              onClick: () => {
                setDuplicateModalData({
                  isOpen: true,
                  setIsOpen: () => {
                    setDuplicateModalData({
                      isOpen: false
                    });
                  },
                  code: row.original.code,
                  duplicateText: row.original.name,
                  recipientCount: row.original.contacts?.ids?.length,
                  giftCount: row.original.products?.length,
                  contactType: row.original.contacts?.ids.length
                    ? CONTACT_TYPE.list
                    : CONTACT_TYPE.tags,
                  giftMessage: row.original.experienceSettings?.messageParams?.message,
                  giftPrice: maxBy(skus, 'price')?.price,
                  digitalGiftCard: getAddonData(row.original.addons, CARDISLE_GREETING_CARD)
                });
              }
            }
          ];

          if (isActive) {
            options.push({
              label: t('deActive'),
              onClick: () => onDeactivateModal(row)
            });
          }

          if (isDeactivated) {
            options.push({
              label: t('reactivate'),
              onClick: () => {
                onReactivateModal(row);
              }
            });
          }

          if (isDraft || isReadyToActivate) {
            options.push({
              label: t('delete'),
              color: 'error',
              style: { color: 'var(--color-error-default)' },
              onClick: () => {
                onDeleteModal(row);
              }
            });
          }

          return (
            <OptionsDropdown
              popperOptions={{
                placement: 'left',
                modifiers: [
                  {
                    name: 'offset',
                    enabled: true,
                    options: {
                      offset: [50, 0]
                    }
                  }
                ]
              }}
              options={options}
            >
              <Icon name="3dot" px={18} />
            </OptionsDropdown>
          );
        }
      }
    ],
    []
  );

  return (
    <S.AutomationTableWrapper>
      <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Trans
          i18nKey="whoseRecognition"
          values={{
            firstName: user?.firstName
          }}
          components={{
            b: <b />
          }}
        />
        <VerticalDivider $height="21px" />
        <Trans
          i18nKey="total"
          values={{
            count: formatNumber(automationList?.pagination?.total)
          }}
          components={{
            b: <b />
          }}
        />
      </Typography>
      <Table
        columns={columns}
        data={automationList?.docs || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: automationList?.pagination?.total
        }}
        filterOptions={automationFilterOptions}
        translations={{
          filteredWithText: t('filteredWith')
        }}
        manualPagination
        loading={isAutomationListLoading}
        setGlobalFilters={getGlobalFilters => {
          const newGlobalFilters = getGlobalFilters(globalFilters);
          setGlobalFilters(newGlobalFilters);
        }}
        setPagination={setPagination}
        pageCount={automationList?.pagination?.pages}
        enabled={{ filter: true, header: true }}
        filterProps={{
          controlWidth: '150px'
        }}
        components={{
          EmptyTable: EmptyAutomationTable,
          Filter,
          SecondHead
        }}
      />
      {isLoading && <AppLoader />}

      {duplicateModalData?.isOpen && (
        <DuplicateModal
          isOpen={duplicateModalData.isOpen}
          onOpen={duplicateModalData.setIsOpen}
          onSubmit={async formValues => {
            const response = await duplicateAutomation({
              code: duplicateModalData.code,
              body: formValues
            });

            navigate(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE(response.code));
          }}
          featureType="automation"
          duplicateText={duplicateModalData.duplicateText}
          recipientCount={duplicateModalData.recipientCount}
          giftCount={duplicateModalData.giftCount}
          contactType={duplicateModalData.contactType}
          giftMessage={duplicateModalData.giftMessage}
          giftPrice={duplicateModalData.giftPrice}
          digitalGiftCard={duplicateModalData.digitalGiftCard}
        />
      )}
    </S.AutomationTableWrapper>
  );
}

export { AutomationTable };
